import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './adminWaybillsList.css';
import Pagination from '../Pagination/Pagination';
import { RoadListService, IRoadListItem, RoadListFilters } from '../../services/roadlist.service';
import WaybillDetails from './WaybillDetails';
import { WaybillStatus, CheckStatus } from '../../types/type';
import { fullName, toBackendDate } from '../../utils/utils';
import { NO_DATE, formatBackendDate, formatBackendDateTime, formatBackendTime } from '../../utils/date.utils';
import { useAppSelector } from '../../store/hooks';

interface IWaybill {
  id: string;
  num: number;
  date: string;
  time: string;
  status: WaybillStatus;
  carBrand: string;
  carNumber: string;
  odometer: number;
  validUntil: string;
  driverName: string;
  operationType: string;
  operationDescription: string;
  originalData?: any; // Для хранения исходных данных API
}

interface IAutoparkInfo {
  name: string;
}

function AdminWaybillsList() {
  const { id: autoparkId } = useParams();
  const user = useAppSelector((state) => state.user.user);
  const [waybills, setWaybills] = useState<IWaybill[]>([]);
  const [autoparkInfo, setAutoparkInfo] = useState<IAutoparkInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('month');
  const [sortBy, setSortBy] = useState('default');
  const [sortOrder, setSortOrder] = useState('asc');
  const [expandedWaybill, setExpandedWaybill] = useState<string | null>(null);
  const [activeFiltersCount, setActiveFiltersCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<RoadListFilters>({
    page: 1,
    limit: 10,
  });
  // Добавляем состояния для выпадающих меню
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);

  // Получение путевых листов из API
  useEffect(() => {
    const fetchWaybills = async () => {
      try {
        setLoading(true);
        setError(null);

        // Определяем autoparkId для запроса
        let requestAutoparkId: string | undefined;

        // Если роль autopark_employee, используем autoparkId из профиля пользователя
        if (user?.role === 'autopark_employee' && user.autoparkIDs?.[0]) {
          requestAutoparkId = user.autoparkIDs[0];
        } else if (autoparkId) {
          // Иначе используем autoparkId из URL (для админов)
          requestAutoparkId = autoparkId;
        }

        // Добавляем autoparkID в фильтры если он определен
        const filtersWithAutopark = { ...filters };
        if (requestAutoparkId) {
          filtersWithAutopark.autoparkID = requestAutoparkId;
        }

        // Используем существующий RoadListService с передачей фильтров
        const response = await RoadListService.getRoadlists(filtersWithAutopark);

        // Преобразование данных из IRoadListItem в IWaybill
        const mappedWaybills: IWaybill[] =
          response && response.data
            ? response.data.map((item: any) => {
                // Разделение даты и времени
                const formattedDate = formatBackendDate(item.date);
                const formattedTime = formatBackendTime(item.date);

                // Определение статуса
                let status: WaybillStatus = 'processing';
                switch (item.status?.toLowerCase()) {
                  case 'finished':
                    status = 'finished';
                    break;
                  case 'completed':
                    status = 'completed';
                    break;
                  case 'canceled':
                  case 'rejected':
                    status = 'rejected';
                    break;
                  case 'waiting':
                  case 'inprogress':
                    status = 'processing';
                    break;
                  default:
                    status = 'processing';
                }

                // Определение операционного типа
                let operationType = '';
                switch (status) {
                  case 'completed':
                    operationType = 'Положительный';
                    break;
                  case 'rejected':
                    operationType = 'Отрицательный';
                    break;
                  default:
                    operationType = 'В обработке';
                }

                // Срок действия
                const validUntil = formatBackendDateTime(item.expiredAt, NO_DATE);

                // Создание объекта IWaybill
                return {
                  id: item.id,
                  date: formattedDate,
                  time: formattedTime,
                  num: item.num || 0,
                  status,
                  carBrand: item.car?.mark || 'Неизвестно',
                  carNumber: item.car?.number || 'Неизвестно',
                  odometer: item.techlist?.odometerReading || 0,
                  validUntil,
                  driverName:
                    fullName({
                      lastName: item.driverLastName,
                      firstName: item.driverFirstName,
                      middleName: item.driverMiddleName,
                    }) || '-',
                  operationType,
                  operationDescription: `Путевой лист №${item.num || 0} ${status === 'completed' ? 'завершен успешно' : status === 'rejected' ? 'отменен' : 'в ожидании'}`,
                  originalData: item, // Сохраняем исходные данные
                };
              })
            : [];

        setWaybills(mappedWaybills);
        setTotalPages(response?.totalPages || 1);

        // Установка информации об автопарке
        if ((requestAutoparkId || autoparkId) && response?.data && response.data.length > 0) {
          setAutoparkInfo({ name: response.data[0]?.autopark?.tradeName || 'ЭПЛ' });
        }

        // Установка первой записи как развернутой по умолчанию
        if (mappedWaybills.length > 0) {
          setExpandedWaybill(mappedWaybills[0].id);
        }
      } catch (err) {
        console.error('Ошибка загрузки путевых листов:', err);
        setError('Не удалось загрузить путевые листы. Пожалуйста, попробуйте позже.');
      } finally {
        setLoading(false);
      }
    };

    fetchWaybills();
  }, [autoparkId, filters, user]);

  // Обновление фильтров
  useEffect(() => {
    const updatedFilters: RoadListFilters = {
      page: currentPage,
      limit: 10,
    };

    // Обновление фильтра по времени
    const now = new Date();
    let startDate: Date | undefined;

    switch (timeFilter) {
      case 'day':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 1);
        break;
      case 'week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case 'all':
      default:
        startDate = undefined;
    }

    if (startDate) {
      updatedFilters.startDate = startDate.toISOString();
      updatedFilters.endDate = now.toISOString();
    }

    // Добавляем фильтр по статусу
    if (statusFilter !== 'all') {
      updatedFilters.status = statusFilter;
    }

    // Подсчет активных фильтров
    let filtersCount = 0;
    if (timeFilter !== 'all') filtersCount++;
    if (statusFilter !== 'all') filtersCount++;

    setActiveFiltersCount(filtersCount || 1);
    setFilters(updatedFilters);
  }, [timeFilter, statusFilter, currentPage]);

  // Функция для закрытия всех дропдаунов при клике вне их области
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.sort-item-dropdown') && !target.closest('.sort-item')) {
        setShowStatusDropdown(false);
        setShowTimeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getStatusText = (status: WaybillStatus) => {
    switch (status) {
      case 'completed':
        return 'Завершен';
      case 'finished':
        return 'Финиширован';
      case 'rejected':
        return 'Отменен';
      case 'processing':
        return 'В ожидании';
      default:
        return '';
    }
  };

  const getStatusColor = (status: WaybillStatus) => {
    switch (status) {
      case 'completed':
        return '#4CAF50';
      case 'finished':
        return '#2196F3';
      case 'rejected':
        return '#F44336';
      case 'processing':
        return '#FF9800';
      default:
        return '#9E9E9E';
    }
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтра
    setShowStatusDropdown(false); // Закрываем дропдаун
  };

  const handleTimeFilterChange = (period: string) => {
    setTimeFilter(period);
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтра
    setShowTimeDropdown(false); // Закрываем дропдаун
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSortChange = (sortField: string) => {
    if (sortBy === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(sortField);
      setSortOrder('asc');
    }
  };

  const toggleWaybillDetails = (waybillId: string) => {
    setExpandedWaybill(expandedWaybill === waybillId ? null : waybillId);
  };

  const toggleStatusDropdown = () => {
    setShowStatusDropdown(!showStatusDropdown);
    setShowTimeDropdown(false);
  };

  const toggleTimeDropdown = () => {
    setShowTimeDropdown(!showTimeDropdown);
    setShowStatusDropdown(false);
  };

  // Убираем клиентскую фильтрацию, так как фильтрация происходит на сервере
  const filteredWaybills = waybills;

  const getStatusFilterLabel = () => {
    switch (statusFilter as WaybillStatus | 'all') {
      case 'all':
        return 'Все статусы';
      case 'completed':
        return 'Завершен';
      case 'rejected':
        return 'Отменен';
      case 'processing':
        return 'В ожидании';
      case 'finished':
        return 'Финиширован';
      default:
        return 'Все статусы';
    }
  };

  const getTimeFilterLabel = () => {
    switch (timeFilter) {
      case 'all':
        return 'За все время';
      case 'day':
        return 'За сутки';
      case 'week':
        return 'За неделю';
      case 'month':
        return 'За месяц';
      case 'year':
        return 'За год';
      default:
        return 'За все время';
    }
  };

  return (
    <div className="admin-waybills-list">
      <div className="waybills-container">
        {/* Основной контент */}
        <div className="content-panel">
          {/* Заголовок и кнопка фильтров */}
          <div className="content-header">
            <h1 className="page-title">
              {autoparkInfo ? `Путевые листы автопарка ${autoparkInfo.name}` : 'Путевые листы'}
            </h1>
            {/* <button className="filters-button">
              <span>Фильтры</span>
              <div className="filters-counter">{activeFiltersCount}</div>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 12L8 4M4 8L12 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button> */}
          </div>

          {/* Сортировка с выпадающими меню */}
          <div className="sorting-section">
            {/* <div className="sort-item" onClick={() => handleSortChange('default')}>
              <span>По умолчанию</span>
              <svg
                className={`sort-icon ${sortBy === 'default' ? 'active' : ''}`}
                width="20"
                height="13"
                viewBox="0 0 19 13"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0 0.75C0 0.335786 0.335786 0 0.75 0H17.75C18.1642 0 18.5 0.335786 18.5 0.75C18.5 1.16421 18.1642 1.5 17.75 1.5H0.75C0.335786 1.5 0 1.16421 0 0.75ZM14.7197 3.21967C15.0126 2.92678 15.4874 2.92678 15.7803 3.21967L18.2803 5.71967C18.5732 6.01256 18.5732 6.48744 18.2803 6.78033C17.9874 7.07322 17.5126 7.07322 17.2197 6.78033L16 5.56066V11.75C16 12.1642 15.6642 12.5 15.25 12.5C14.8358 12.5 14.5 12.1642 14.5 11.75V5.56066L13.2803 6.78033C12.9874 7.07322 12.5126 7.07322 12.2197 6.78033C11.9268 6.48744 11.9268 6.01256 12.2197 5.71967L14.7197 3.21967ZM0 5.75C0 5.33579 0.335786 5 0.75 5H8.75C9.16421 5 9.5 5.33579 9.5 5.75C9.5 6.16421 9.16421 6.5 8.75 6.5H0.75C0.335786 6.5 0 6.16421 0 5.75ZM0 10.75C0 10.3358 0.335786 10 0.75 10H9.75C10.1642 10 10.5 10.3358 10.5 10.75C10.5 11.1642 10.1642 11.5 9.75 11.5H0.75C0.335786 11.5 0 11.1642 0 10.75Z"
                  fill={sortBy === 'default' ? '#1D61E7' : '#CACACA'}
                />
              </svg>
            </div> */}

            {/* Статус с выпадающим меню */}
            <div className="sort-item-container">
              <div className="sort-item" onClick={toggleStatusDropdown}>
                <span>{getStatusFilterLabel()}</span>
                <svg
                  className={`chevron-icon ${showStatusDropdown ? 'expanded' : ''}`}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              {showStatusDropdown && (
                <div className="sort-item-dropdown">
                  <div
                    className={`dropdown-item ${statusFilter === 'all' ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange('all')}
                  >
                    Все статусы
                  </div>
                  <div
                    className={`dropdown-item ${statusFilter === 'completed' ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange('completed')}
                  >
                    Завершен
                  </div>
                  <div
                    className={`dropdown-item ${statusFilter === 'rejected' ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange('rejected')}
                  >
                    Отменен
                  </div>

                  <div
                    className={`dropdown-item ${statusFilter === 'processing' ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange('processing')}
                  >
                    В ожидании
                  </div>
                  <div
                    className={`dropdown-item ${statusFilter === 'finished' ? 'active' : ''}`}
                    onClick={() => handleStatusFilterChange('finished')}
                  >
                    Финиширован
                  </div>
                </div>
              )}
            </div>

            {/* Временной фильтр с выпадающим меню */}
            <div className="sort-item-container">
              <div className="sort-item" onClick={toggleTimeDropdown}>
                <span>{getTimeFilterLabel()}</span>
                <svg
                  className={`chevron-icon ${showTimeDropdown ? 'expanded' : ''}`}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              {showTimeDropdown && (
                <div className="sort-item-dropdown">
                  <div
                    className={`dropdown-item ${timeFilter === 'all' ? 'active' : ''}`}
                    onClick={() => handleTimeFilterChange('all')}
                  >
                    За все время
                  </div>
                  <div
                    className={`dropdown-item ${timeFilter === 'day' ? 'active' : ''}`}
                    onClick={() => handleTimeFilterChange('day')}
                  >
                    За сутки
                  </div>
                  <div
                    className={`dropdown-item ${timeFilter === 'week' ? 'active' : ''}`}
                    onClick={() => handleTimeFilterChange('week')}
                  >
                    За неделю
                  </div>
                  <div
                    className={`dropdown-item ${timeFilter === 'month' ? 'active' : ''}`}
                    onClick={() => handleTimeFilterChange('month')}
                  >
                    За месяц
                  </div>
                  <div
                    className={`dropdown-item ${timeFilter === 'year' ? 'active' : ''}`}
                    onClick={() => handleTimeFilterChange('year')}
                  >
                    За год
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Таблица путевых листов */}
          <div className="waybills-table">
            {loading ? (
              <div className="loading-state">Загрузка путевых листов...</div>
            ) : error ? (
              <div className="error-state">{error}</div>
            ) : filteredWaybills.length === 0 ? (
              <div className="empty-state">Путевые листы не найдены</div>
            ) : (
              filteredWaybills.map((waybill) => (
                <div key={waybill.id} className="waybill-item">
                  <div className="waybill-row">
                    <div className="waybill-cell waybill-cell--date">
                      <div className="cell-label">Путевой лист №{waybill.num}</div>
                      <div className="cell-value">{waybill.date}</div>
                    </div>
                    <div className="waybill-cell waybill-cell--time">
                      <div className="cell-label">Время</div>
                      <div className="cell-value">{waybill.time}</div>
                    </div>
                    <div className="waybill-cell waybill-cell--status">
                      <div className="cell-label">Статус</div>
                      <div className="status-indicator">
                        <div className="status-dot" style={{ backgroundColor: getStatusColor(waybill.status) }}></div>
                        <span className="status-text">{getStatusText(waybill.status)}</span>
                      </div>
                    </div>
                    <div className="waybill-cell waybill-cell--car">
                      <div className="cell-label">Марка авто</div>
                      <div className="cell-value">{waybill.carBrand}</div>
                    </div>
                    <div className="waybill-cell waybill-cell--number">
                      <div className="cell-label">Номер авто</div>
                      <div className="cell-value">{waybill.carNumber}</div>
                    </div>
                    <div className="waybill-cell waybill-cell--odometer">
                      <div className="cell-label">Одометр</div>
                      <div className="cell-value">{waybill.odometer.toLocaleString()}</div>
                    </div>
                    <div className="waybill-cell waybill-cell--valid">
                      <div className="cell-label">Срок действия</div>
                      <div className="cell-value">{waybill.validUntil}</div>
                    </div>
                    <div className="waybill-cell waybill-cell--actions">
                      <button className="details-button" onClick={() => toggleWaybillDetails(waybill.id)}>
                        <span>Подробнее</span>
                        <svg
                          className={`chevron-icon ${expandedWaybill === waybill.id ? 'expanded' : ''}`}
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M4 6L8 10L12 6"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Развернутые детали */}
                  {expandedWaybill === waybill.id && waybill.originalData && (
                    <WaybillDetails waybill={waybill} mappedItem={waybill.originalData} />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Пагинация */}
          {totalPages > 1 && (
            <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center' }}>
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminWaybillsList;
